import {
	type FormEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react'

const initSelected = () => new Set<string>()
export interface WithId {
	id: string
}
export function useSelectedControls({ items = [] }: { items?: WithId[] }) {
	const selected = useMemo(initSelected, [])
	const [_, setRerender] = useState(0)
	const [areAllSelected, setAreAllSelected] = useState(false)
	const headCheckRef = useRef<HTMLInputElement | null>(null)

	const addIds = useCallback(
		(items: WithId[]) => items.forEach(item => selected.add(item.id)),
		[selected]
	)

	useEffect(() => {
		areAllSelected && addIds(items)
	}, [items, areAllSelected, addIds])

	const handleRowCheck = (event: FormEvent<HTMLInputElement>) => {
		const input = event.currentTarget
		const id = input.value
		const checked = input.checked

		checked ? selected.add(id) : selected.delete(id)

		recheck(items)
	}

	const recheck = (items: WithId[]) => {
		const checkbox = headCheckRef.current
		if (!checkbox) {
			return
		}

		let willChecked = selected.size >= items.length

		if (willChecked) {
			willChecked = items.every(item => selected.has(item.id))

			!willChecked && setAreAllSelected(false)
		}

		checkbox.checked = willChecked
		checkbox.indeterminate = Boolean(selected.size && !willChecked)

		setRerender(prev => ++prev)
	}

	const removeId = (id: string) => {
		selected.delete(id)
		recheck(items)
	}

	const selectAll = () => {
		addIds(items)
		const checkbox = headCheckRef.current
		if (checkbox) {
			checkbox.checked = true
			checkbox.indeterminate = false
		}
		setRerender(prev => ++prev)
	}

	const unSelectAll = useCallback(() => {
		selected.clear()
		setAreAllSelected(false)

		const checkbox = headCheckRef.current
		if (checkbox) {
			checkbox.checked = false
			checkbox.indeterminate = false
		}

		setRerender(prev => ++prev)
	}, [selected])

	function handleSelectAll() {
		addIds(items)
		setAreAllSelected(true)
		setRerender(prev => ++prev)
	}

	function handleHeadCheck(event: FormEvent<HTMLInputElement>) {
		const input = event.currentTarget
		const checked = input.checked

		checked ? selectAll() : unSelectAll()
	}

	return {
		areAllSelected,
		handleHeadCheck,
		handleRowCheck,
		handleSelectAll,
		isActiveHeader: Boolean(selected.size),
		removeId,
		selectAllRef: headCheckRef,
		selected,
		setAreAllSelected,
		unSelectAll
	}
}
